import { useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Megaphone, AlertOctagon, Info, Star, X, CalendarClock, ArrowRight, BarChart3 } from "lucide-react";
import { PollBlock } from "@/components/tracker/AnnouncementPoll";
import { useAnnouncements } from "@/context/AnnouncementsContext";
import { useBilling } from "@/context/BillingContext";

const EASE = [0.22, 1, 0.36, 1];

export const LEVELS = {
  info: { label: "Info", color: "#7FD1FF", icon: Info },
  important: { label: "Important", color: "#FFB020", icon: Star },
  critical: { label: "Critical", color: "#FF6B81", icon: AlertOctagon },
};
export const POLL_STYLE = { label: "Poll", color: "#00FF66", icon: BarChart3 };
export const styleOf = (a) => (a.kind === "poll" ? POLL_STYLE : LEVELS[a.level] || LEVELS.info);

export const fmtDay = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export const daysUntil = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((new Date(y, m - 1, d) - today) / 86400000);
};

export const countdown = (iso) => {
  const n = daysUntil(iso);
  if (n === 0) return "Today";
  if (n === 1) return "Tomorrow";
  return n > 0 ? `in ${n} days` : `${-n} days ago`;
};

function KeyDate({ iso, color }) {
  return (
    <div className="mt-5 flex items-center gap-3 rounded-xl border px-4 py-3" style={{ borderColor: `${color}40`, backgroundColor: `${color}0f` }} data-testid="announcement-popup-key-date">
      <CalendarClock className="h-5 w-5 shrink-0" style={{ color }} />
      <div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-white/40">Key date</p>
        <p className="font-display text-base font-bold text-white">
          {fmtDay(iso)} <span className="ml-1 text-xs font-semibold" style={{ color }}>{countdown(iso)}</span>
        </p>
      </div>
    </div>
  );
}

function Popup({ ann, remaining, onAck, onVoted, onLater }) {
  const lvl = styleOf(ann);
  const critical = ann.level === "critical";
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="announcement-popup">
      <motion.div className="absolute inset-0 bg-black/70 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} />
      <motion.div
        key={ann.id}
        initial={{ opacity: 0, y: 18, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.98 }}
        transition={{ duration: 0.32, ease: EASE }}
        className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-white/10 bg-[#0d0f12] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)]"
      >
        <div className="h-1 w-full" style={{ backgroundColor: lvl.color, boxShadow: `0 0 24px ${lvl.color}` }} />
        {!critical && (
          <button onClick={onLater} className="absolute right-5 top-6 text-white/35 transition-colors hover:text-white" aria-label="Remind me later" data-testid="announcement-popup-later">
            <X className="h-4 w-4" />
          </button>
        )}
        <div className="p-7">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ backgroundColor: `${lvl.color}1a`, color: lvl.color }}>
              {ann.kind === "poll" ? <BarChart3 className="h-4 w-4" /> : <Megaphone className="h-4 w-4" />}
            </span>
            <span className="rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-black" style={{ backgroundColor: lvl.color }} data-testid="announcement-popup-level">
              {lvl.label}
            </span>
            <span className="truncate text-[11px] font-semibold uppercase tracking-widest text-white/35">
              {ann.project_name ? ann.project_name : "Whole workspace"}
            </span>
          </div>
          <h2 className="font-display mt-5 break-words text-2xl font-extrabold leading-tight tracking-tight text-white" data-testid="announcement-popup-title">{ann.title}</h2>
          {ann.body && <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-relaxed text-white/65" data-testid="announcement-popup-body">{ann.body}</p>}
          {ann.poll && <PollBlock ann={ann} onVoted={onVoted} />}
          {ann.key_date && <KeyDate iso={ann.key_date} color={lvl.color} />}
          <p className="mt-5 text-xs text-white/35">Posted by {ann.created_by_name}</p>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-white/[0.06] bg-white/[0.02] px-7 py-4">
          <span className="text-xs text-white/35" data-testid="announcement-popup-remaining">{remaining > 0 ? `${remaining} more after this` : ""}</span>
          <button
            onClick={onAck}
            autoFocus
            className="rounded-full px-5 py-2.5 text-xs font-black uppercase tracking-widest text-black transition-transform duration-200 hover:-translate-y-0.5"
            style={{ backgroundColor: lvl.color, boxShadow: `0 8px 26px -8px ${lvl.color}` }}
            data-testid="announcement-popup-ack"
          >
            {critical ? "Acknowledge" : "Got it"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export function AnnouncementPopup() {
  const ctx = useAnnouncements();
  const [later, setLater] = useState([]);
  const enabled = useBilling()?.has("announcements");
  if (!ctx || !enabled) return null;
  const queue = ctx.items
    .filter((a) => a.active && !a.acknowledged && !later.includes(a.id))
    .sort((a, b) => (a.level === "critical" ? 0 : 1) - (b.level === "critical" ? 0 : 1));
  const ann = queue[0];
  return createPortal(
    <AnimatePresence>
      {ann && <Popup ann={ann} remaining={queue.length - 1} onAck={() => ctx.acknowledge(ann.id)} onVoted={(p) => ctx.setPoll(ann.id, p)} onLater={() => setLater((l) => [...l, ann.id])} />}
    </AnimatePresence>,
    document.body,
  );
}

export function AnnouncementBanners() {
  const ctx = useAnnouncements();
  const enabled = useBilling()?.has("announcements");
  const pinned = (enabled ? ctx?.items || [] : []).filter((a) => a.pinned && a.key_date && daysUntil(a.key_date) >= 0);
  if (!pinned.length) return null;
  return (
    <div className="no-print mb-6 space-y-2" data-testid="announcement-banners">
      {pinned.map((a) => (
        <Link
          key={a.id}
          to="/app/announcements"
          className="group flex items-center gap-3 rounded-2xl border border-[#FF6B81]/30 bg-[#FF6B81]/[0.07] px-5 py-3 text-sm transition-colors duration-200 hover:border-[#FF6B81]/60"
          data-testid={`announcement-banner-${a.id}`}
        >
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FF6B81] opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#FF6B81]" />
          </span>
          <span className="min-w-0 truncate text-white/75">
            <span className="font-bold text-white">{a.title}</span>
            <span className="text-white/40"> · {fmtDay(a.key_date)}</span>
          </span>
          <span className="ml-auto flex shrink-0 items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#FF6B81]">
            {countdown(a.key_date)} <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
          </span>
        </Link>
      ))}
    </div>
  );
}
